import L from 'leaflet'
import 'leaflet-fullscreen'
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css'
import React, { useContext, useEffect, useState } from 'react'
import { v1 as uuidv1 } from 'uuid'
import {
  MapProviderKey,
  getTileLayerArgs,
  freeMapOverlayProviders,
} from '../../../constants/leafletMapProviders'
import { LeafletContext } from '../contexts/LeafletContext'

type LeafletMapProps = {
  className?: string
  style?: object
  opts: L.MapOptions
  mapProviders: MapProviderKey[]
  overlayLayersProviders?: MapProviderKey[]
}

const defaultStyle = {
  height: '100%',
}

/**
 * Creates a map of Leaflet tile layers from an array of map provider keys
 * @param providers - Array of map provider keys to convert to tile layers
 * @returns Object mapping provider keys to Leaflet tile layers
 */
function createTileLayersFromProviders(
  providers: MapProviderKey[]
): Record<string, L.TileLayer> {
  return providers.reduce((acc, mapProvider) => {
    return {
      ...acc,
      [mapProvider]: L.tileLayer(...getTileLayerArgs(mapProvider)),
    }
  }, {})
}

export default function LeafletMap({
  opts,
  style = {},
  className,
  mapProviders,
  overlayLayersProviders = freeMapOverlayProviders,
}: LeafletMapProps) {
  const { dispatch } = useContext(LeafletContext)
  const [map, setMap] = useState<L.Map | undefined>(undefined)
  const [mapItemId] = useState(`map-${uuidv1()}`)

  useEffect(() => {
    if (map) return
    const myMap = L.map(mapItemId, opts)

    //@ts-ignore
    myMap.addControl(new L.Control.Fullscreen())
    setMap(myMap)

    const baseLayers = createTileLayersFromProviders(mapProviders)
    const overlayLayers = createTileLayersFromProviders(overlayLayersProviders)

    if (mapProviders.length > 1) {
      L.control
        .layers(baseLayers, overlayLayers, {
          position: 'bottomright',
          hideSingleBase: true,
        })
        .addTo(myMap)
    }

    // @ts-ignore
    baseLayers[mapProviders[0]].addTo(myMap)

    dispatch({ type: 'init_map', map: myMap })
    return () => dispatch({ type: 'reset' })
    // eslint-disable-next-line
  }, [mapItemId])

  return (
    <>
      {typeof document !== 'undefined' ? (
        <div
          id={mapItemId}
          style={{ ...defaultStyle, ...style }}
          className={className}
        />
      ) : null}
    </>
  )
}
